import { useMutation, useQueryClient } from '@tanstack/react-query';
import { api } from '../../lib/api';
import { fmtDate } from '../../lib/utils';
import { toast } from '../../stores/toast.store';
import { confirm as confirmDialog } from '../../stores/confirm.store';
import { nextOccurrence, type TaskItem, type TaskListResponse, type TaskRecurrenceKey, type TaskStatusKey } from './tasksApi';

/**
 * Azioni condivise sui task (lista, dettaglio, stream Oggi).
 *
 * Stesso pattern di useDocActions: ogni mutation invalida ['tasks'] (e
 * ['project'] perché la kanban di progetto legge gli stessi record), gestisce
 * toast/undo; i consumer chiamano solo `.mutate(...)` e usano `.isPending`.
 */

export interface QuickCreateArgs {
  title: string;
  assigneeId?: string | null;
  dueDate?: string | null;
  projectId?: string | null;
  clientId?: string | null;
}

/** Dati del task chiuso che servono al toast "Completato" (undo + prossima occorrenza). */
export interface CompletedVars {
  id: string;
  title?: string;
  prevStatus?: TaskStatusKey;
  dueDate?: string | null;
  recurrence?: TaskRecurrenceKey;
}

/** Spostamento nella kanban a due colonne (Da fare / Completate). */
export interface KanbanMoveArgs extends CompletedVars {
  /** Stato di arrivo: DONE per Completate, lo stato aperto (TODO o quello che aveva) per Da fare. */
  status: TaskStatusKey;
  position: number;
  order: number;
  /** complete = Da fare → Completate, reopen = Completate → Da fare, reorder = stessa colonna. */
  kind: 'complete' | 'reopen' | 'reorder';
  /** Query della kanban da aggiornare in modo ottimistico. */
  queryKey: readonly unknown[];
}

export function useTaskActions() {
  const qc = useQueryClient();
  const invalidate = () => {
    qc.invalidateQueries({ queryKey: ['tasks'] });
    qc.invalidateQueries({ queryKey: ['project'] });
  };

  /**
   * Toast "Task completato" con Annulla (riporta allo stato di prima). Se il
   * task è ricorrente l'API crea da sola la prossima occorrenza: il toast lo
   * dice, così il "nuovo" task in Da fare non sembra un doppione.
   * Condiviso da Completa (lista/dettaglio) e dal drag in Completate (kanban).
   */
  const completedToast = (vars: CompletedVars) => {
    const next = vars.recurrence ? nextOccurrence(vars.dueDate, vars.recurrence) : null;
    toast.undo('Task completato', {
      description: next ? `${vars.title ?? ''} · il prossimo è in lista per il ${fmtDate(next)}`.trim() : vars.title,
      onUndo: () => {
        api.patch(`/tasks/${vars.id}`, { status: vars.prevStatus && vars.prevStatus !== 'DONE' ? vars.prevStatus : 'TODO' })
          .then(invalidate)
          .catch((e: any) => toast.alert('Riapertura non riuscita', e?.message ?? 'Riprova'));
      },
    });
  };

  /** Completa: PATCH status DONE + toast con Annulla. */
  const complete = useMutation({
    mutationFn: ({ id }: CompletedVars) => api.patch<TaskItem>(`/tasks/${id}`, { status: 'DONE' }),
    onSuccess: (_r, vars) => { invalidate(); completedToast(vars); },
    onError: (e: any) => toast.alert('Task non completato', e?.message ?? 'Riprova'),
  });

  /**
   * Drag nella kanban: POST /tasks/:id/move { status, position, order }.
   * Ottimistico: la card cambia colonna/posizione subito (cache della kanban),
   * rollback su errore; in Completate = stesso toast/undo di "Completa",
   * in Da fare = "Task riaperto". Il server mette completedAt e, se ricorrente,
   * crea la prossima occorrenza (come il PATCH).
   */
  const kanbanMove = useMutation({
    mutationFn: ({ id, status, position, order }: KanbanMoveArgs) =>
      api.post<TaskItem>(`/tasks/${id}/move`, { status, position, order }),
    onMutate: (vars) => {
      const prev = qc.getQueryData<TaskListResponse>(vars.queryKey);
      if (prev) {
        const moving = prev.items.find((t) => t.id === vars.id);
        if (moving) {
          const others = prev.items.filter((t) => t.id !== vars.id);
          const updated: TaskItem = {
            ...moving,
            status: vars.status,
            order: vars.order,
            completedAt: vars.status === 'DONE' ? (moving.completedAt ?? new Date().toISOString()) : null,
          };
          qc.setQueryData<TaskListResponse>(vars.queryKey, { ...prev, items: [...others, updated] });
        }
      }
      void qc.cancelQueries({ queryKey: vars.queryKey });
      return { prev };
    },
    onError: (e: any, vars, ctx) => {
      if (ctx?.prev) qc.setQueryData(vars.queryKey, ctx.prev);
      toast.alert('Spostamento non riuscito', e?.message ?? 'Riprova');
    },
    onSuccess: (_r, vars) => {
      if (vars.kind === 'complete') completedToast(vars);
      else if (vars.kind === 'reopen') toast.ok('Task riaperto', vars.title);
    },
    onSettled: invalidate,
  });

  /** Riapri: torna a Da fare. */
  const reopen = useMutation({
    mutationFn: ({ id }: { id: string; title?: string }) => api.patch<TaskItem>(`/tasks/${id}`, { status: 'TODO' }),
    onSuccess: (_r, vars) => { invalidate(); toast.ok('Task riaperto', vars.title); },
    onError: (e: any) => toast.alert('Task non riaperto', e?.message ?? 'Riprova'),
  });

  /** PATCH parziale (campi del dettaglio, salvataggio al cambio). */
  const update = useMutation({
    mutationFn: ({ id, data }: { id: string; data: Record<string, unknown> }) => api.patch<TaskItem>(`/tasks/${id}`, data),
    onSuccess: invalidate,
    onError: (e: any) => toast.alert('Modifica non salvata', e?.message ?? 'Riprova'),
  });

  /** Elimina (soft) con conferma. Ritorna null se l'utente annulla. */
  const remove = useMutation({
    mutationFn: async ({ id, title }: { id: string; title?: string }) => {
      const ok = await confirmDialog({
        title: 'Eliminare questo task?',
        body: title ? `"${title}" sparisce dalla lista.` : undefined,
        danger: true,
        confirmLabel: 'Elimina',
        icon: 'trash',
      });
      if (!ok) return null;
      await api.delete(`/tasks/${id}`);
      return true;
    },
    onSuccess: (done, vars) => {
      if (!done) return;
      // Non rifetchare il dettaglio del task appena eliminato (risponderebbe 404):
      // lista, riepilogo e kanban di progetto sì.
      qc.invalidateQueries({ queryKey: ['tasks'], predicate: (q) => q.queryKey[1] !== 'detail' });
      qc.invalidateQueries({ queryKey: ['project'] });
      toast.ok('Task eliminato', vars.title);
    },
    onError: (e: any) => toast.alert('Eliminazione non riuscita', e?.message ?? 'Riprova'),
  });

  /** Quick add: titolo + assegnatario + scadenza. */
  const quickCreate = useMutation({
    mutationFn: (args: QuickCreateArgs) => api.post<TaskItem>('/tasks', {
      title: args.title,
      status: 'TODO',
      priority: 'MEDIA',
      recurrence: 'NONE',
      isPrivate: false,
      assigneeId: args.assigneeId || null,
      dueDate: args.dueDate || null,
      projectId: args.projectId || null,
      clientId: args.clientId || null,
    }),
    onSuccess: (_r, vars) => { invalidate(); toast.ok('Task creato', vars.title); },
    onError: (e: any) => toast.alert('Task non creato', e?.message ?? 'Riprova'),
  });

  return { complete, reopen, update, remove, quickCreate, kanbanMove };
}
